import { useEffect, useRef, useState, useId } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { Link } from "react-router-dom";
import { MessageCircle, ExternalLink, Copy, Send } from "lucide-react";
import { apiClient } from "../../../lib/api";
import {
  CONTENT_KINDS,
  RELATION_LABELS,
  selectedData,
  type CommunityContentBlock,
  type ContentData,
} from "../../../lib/communityContent";
import "../../../styles/community-content.css";
function GptChat({
  sourceId,
  examples,
}: {
  sourceId: string;
  examples: string[];
}) {
  const { user } = useAuth();
  const inputId = useId();
  const [open, setOpen] = useState(false),
    [input, setInput] = useState(""),
    [messages, setMessages] = useState<{ role: string; content: string }[]>([]),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const sending = useRef(false);
  const generation = useRef(0);
  useEffect(() => {
    generation.current++;
    setOpen(false);
    setMessages([]);
    setInput("");
    setError("");
    setBusy(false);
    return () => {
      generation.current++;
    };
  }, [sourceId, user?.id]);
  async function send(value = input) {
    if (sending.current || !value.trim()) return;
    sending.current = true;
    setBusy(true);
    setError("");
    const version = generation.current;
    try {
      const r = await apiClient.sendCustomGptMessage(
        Number(sourceId),
        value.trim(),
      );
      if (version !== generation.current) return;
      setMessages(r.messages);
      setInput("");
    } catch (e) {
      if (version === generation.current)
        setError(e instanceof Error ? e.message : "대화에 실패했습니다.");
    } finally {
      sending.current = false;
      if (version === generation.current) setBusy(false);
    }
  }
  async function start() {
    setOpen(true);
    setBusy(true);
    setError("");
    const version = generation.current;
    try {
      const r = await apiClient.getCustomGptChat(Number(sourceId));
      if (version === generation.current) setMessages(r.messages);
    } catch {
      if (version === generation.current)
        setError("이전 대화를 불러오지 못했습니다. 다시 열어주세요.");
    } finally {
      if (version === generation.current) setBusy(false);
    }
  }
  if (!user)
    return (
      <Link
        className="fe-community-button"
        to={`/login?next=${encodeURIComponent(window.location.pathname)}`}
      >
        로그인하고 이 GPT와 대화하기
      </Link>
    );
  return (
    <div className="fe-content-gpt-chat">
      {!open ? (
        <>
          <div className="fe-content-examples">
            {examples.map((e, i) => (
              <span key={i}>{e}</span>
            ))}
          </div>
          <button type="button" className="fe-community-button" onClick={start}>
            <MessageCircle size={16} />이 GPT와 대화 시작
          </button>
        </>
      ) : (
        <>
          <div className="fe-content-chat-top">
            <span>나와 GPT의 대화 · 게시글에 공개되지 않아요</span>
            <button type="button" onClick={() => setOpen(false)}>
              접기
            </button>
          </div>
          <div
            className="fe-content-chat-log"
            role="log"
            aria-label="내 GPT 대화"
          >
            {messages.length ? (
              messages.map((m, i) => (
                <div key={i} className={m.role === "user" ? "is-user" : ""}>
                  <strong>{m.role === "user" ? "나" : "GPT"}</strong>
                  <p>{m.content}</p>
                </div>
              ))
            ) : (
              <p>아래에 질문을 적어 대화를 시작하세요.</p>
            )}
          </div>
          <div className="fe-content-examples">
            {examples.slice(0, 3).map((e, i) => (
              <button
                disabled={busy}
                type="button"
                key={i}
                onClick={() => setInput(e)}
              >
                {e}
              </button>
            ))}
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void send();
            }}
          >
            <label className="sr-only" htmlFor={inputId}>
              GPT에게 질문
            </label>
            <textarea
              id={inputId}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="이 GPT에게 질문해보세요"
              rows={2}
            />
            <button
              className="fe-community-button"
              disabled={busy || !input.trim()}
              type="submit"
            >
              <Send size={16} />
              {busy ? "답변 중" : "보내기"}
            </button>
          </form>
          {error && <p role="alert">{error}</p>}
        </>
      )}
    </div>
  );
}
function Graph({ data }: { data: ContentData }) {
  const [kind, setKind] = useState("");
  const available = [...new Set(data.edges?.map((e) => e.kind) || [])];
  const current = available.includes(kind) ? kind : available[0];
  const nodes = data.nodes || [];
  const coordinates = new Map(
    nodes.map((n, i) => [
      n.id,
      {
        x: 250 + 190 * Math.cos((2 * Math.PI * i) / Math.max(1, nodes.length)),
        y: 240 + 175 * Math.sin((2 * Math.PI * i) / Math.max(1, nodes.length)),
      },
    ]),
  );
  return (
    <>
      <p className="fe-content-note">
        가명 처리된 관계도 · 이름·학번과의 연결 정보는 포함하지 않습니다.
      </p>
      {available.length > 0 && (
        <label>
          관계 유형{" "}
          <select value={current} onChange={(e) => setKind(e.target.value)}>
            {available.map((k) => (
              <option key={k} value={k}>
                {RELATION_LABELS[k] || k}
              </option>
            ))}
          </select>
        </label>
      )}
      <svg
        viewBox="0 0 500 480"
        className="fe-content-graph"
        role="img"
        aria-label={`${RELATION_LABELS[current] || "선택된"} 관계도, 참가자 ${nodes.length}명`}
      >
        {(data.edges || [])
          .filter((e) => e.kind === current)
          .map((e, i) => {
            const a = coordinates.get(e.from),
              b = coordinates.get(e.to);
            if (!a || !b) return null;
            const dx = b.x - a.x,
              dy = b.y - a.y,
              l = Math.hypot(dx, dy) || 1;
            const tip = { x: b.x - (dx / l) * 15, y: b.y - (dy / l) * 15 };
            return (
              <g key={i}>
                <line x1={a.x} y1={a.y} x2={tip.x} y2={tip.y} />
                <polygon
                  points={`${tip.x},${tip.y} ${tip.x - (dx / l) * 8 - (dy / l) * 4},${tip.y - (dy / l) * 8 + (dx / l) * 4} ${tip.x - (dx / l) * 8 + (dy / l) * 4},${tip.y - (dy / l) * 8 - (dx / l) * 4}`}
                />
              </g>
            );
          })}
        {nodes.map((n) => {
          const p = coordinates.get(n.id)!;
          return (
            <g key={n.id}>
              <circle cx={p.x} cy={p.y} r={13} />
              <text x={p.x} y={p.y + 29} textAnchor="middle">
                {n.label}
              </text>
            </g>
          );
        })}
      </svg>
      <details>
        <summary>관계 목록으로 보기</summary>
        <ul>
          {(data.edges || [])
            .filter((e) => e.kind === current)
            .map((e, i) => (
              <li key={i}>
                {nodes.find((n) => n.id === e.from)?.label} →{" "}
                {nodes.find((n) => n.id === e.to)?.label}
              </li>
            ))}
        </ul>
      </details>
      {!!data.mood?.length && (
        <div className="fe-content-mood">
          <strong>전체 응답의 기분 분포</strong>
          {data.mood.map((m) => (
            <div key={m.value}>
              <span>{m.value}점</span>
              <meter
                min={0}
                max={Math.max(1, ...data.mood!.map((r) => r.count))}
                value={m.count}
              />
              <span>{m.count}명</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
export function ContentBlock({
  block,
  preview = false,
}: {
  block: CommunityContentBlock;
  preview?: boolean;
}) {
  const d = selectedData(block);
  const meta = CONTENT_KINDS.find((k) => k.kind === block.kind);
  const [copied, setCopied] = useState(false),
    [copyError, setCopyError] = useState(false);
  if (block.unavailable)
    return (
      <section className="fe-content-block">
        <span>{meta?.label}</span>
        <p>현재 공개되지 않거나 삭제된 콘텐츠입니다.</p>
      </section>
    );
  return (
    <section
      className={`fe-content-block fe-content-${block.kind}`}
      aria-label={`${meta?.label} 콘텐츠`}
    >
      <header>
        <span>{meta?.label}</span>
        <small>
          {block.mode === "reference" ? "공개 원본 연결" : "게시 시점의 공유본"}
        </small>
      </header>
      <h3>{d.title}</h3>
      {d.description && (
        <p className="fe-content-description">{d.description}</p>
      )}
      {block.kind === "gpt" &&
        (preview ? (
          <>
            <div className="fe-content-examples">
              {d.examples?.map((e, i) => (
                <span key={i}>{e}</span>
              ))}
            </div>
            <p className="fe-content-note">
              게시 후 독자가 이 자리에서 GPT와 대화할 수 있어요.
            </p>
          </>
        ) : (
          <GptChat sourceId={block.sourceId!} examples={d.examples || []} />
        ))}
      {(block.kind === "quiz" || block.kind === "peerquiz") && (
        <div className="fe-content-questions">
          <p>문제 {d.questions?.length || 0}개</p>
          {d.questions?.map((q, i) => (
            <details key={i} open={i === 0}>
              <summary>
                {i + 1}. {q.text}
              </summary>
              {q.image && (
                <img src={q.image} alt="문제 이미지" loading="lazy" />
              )}
              {q.choices.length > 0 && (
                <ol>
                  {q.choices.map((c, j) => (
                    <li key={j}>
                      {c}
                      {q.choiceImages?.[j] && (
                        <img
                          src={q.choiceImages[j]}
                          alt={`${j + 1}번 보기 이미지`}
                          loading="lazy"
                        />
                      )}
                    </li>
                  ))}
                </ol>
              )}
              <details className="fe-content-answer">
                <summary>정답 확인</summary>
                <p>{q.answer}</p>
              </details>
            </details>
          ))}
        </div>
      )}
      {block.kind === "wordcloud" && (
        <>
          <div
            className="fe-content-cloud-words"
            role="img"
            aria-label="선택한 단어와 빈도로 구성한 워드클라우드"
          >
            {d.words?.map((w, i) => (
              <span
                key={w.text}
                title={`${w.text}: ${w.count}회`}
                style={{
                  fontSize: `${14 + Math.sqrt(w.count / Math.max(1, ...d.words!.map((x) => x.count))) * 30}px`,
                  color: ["#526a2e", "#776887", "#906b48", "#476f68"][i % 4],
                }}
              >
                {w.text}
              </span>
            ))}
          </div>
          <details>
            <summary>단어별 빈도 보기</summary>
            <ul>
              {d.words?.map((w) => (
                <li key={w.text}>
                  {w.text} · {w.count}회
                </li>
              ))}
            </ul>
          </details>
        </>
      )}
      {block.kind === "sociometry" && <Graph data={d} />}
      {block.kind === "prompt" && (
        <>
          <details open>
            <summary>공유한 프롬프트</summary>
            <pre>{d.prompt}</pre>
          </details>
          <button
            type="button"
            className="fe-community-button"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(d.prompt || "");
                setCopied(true);
                setCopyError(false);
              } catch {
                setCopyError(true);
              }
            }}
          >
            <Copy size={16} />
            {copied ? "복사했어요" : "프롬프트 복사"}
          </button>
          {copyError && (
            <p role="alert">
              복사하지 못했습니다. 위의 내용을 선택해 복사해주세요.
            </p>
          )}
        </>
      )}
      {(block.kind === "book" || block.kind === "vibe") && (
        <>
          {d.image && (
            <img
              className="fe-content-cover"
              src={d.image}
              alt={d.title}
              loading="lazy"
            />
          )}
          {d.author && <p>저자 · {d.author}</p>}
          {block.kind === "vibe" && d.url && (
            <a
              className="fe-community-button"
              href={d.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink size={16} />
              작품 사용하기
            </a>
          )}
        </>
      )}
      {block.href && block.kind !== "gpt" && (
        <Link className="fe-content-source" to={block.href}>
          {block.kind === "quiz" ? "이 문제 세트 사용하기" : "원본 소개 보기"}{" "}
          <ExternalLink size={14} />
        </Link>
      )}
    </section>
  );
}
